/**
 * useArchiveTaskPolling · 异步归档任务进度轮询
 *
 * photobook 建夹同步返回 folderID，但照片归档是 photobook 内部异步搬运（按 rule
 * 扫描全量照片）。前端无 server push 通道，只能轮询 /web/folder/statistic 拿
 * photoCount 推断进度。
 *
 * 策略
 *   · 间隔：10s（用户拍板）· 团队 RPC 容量充裕，10s 比 30s 体感更"实时"
 *   · 触发：仅 active tasks 非空时才起 timer，全部完成自动停轮询
 *   · 完成判定：photoCount >= expectedCount × 0.9 → completed
 *     （estimatedCount 是 LLM 估算可能偏差，给 90% 容差避免永远等不到）
 *   · 超时：createdAt 超过 10 分钟仍未达标 → timeout（保留在列表，停轮询）
 *
 * 挂载点：AIChatDrawer 顶层（与 useAIClassifySession 平级），visible 变化无关
 *   —— 用户关抽屉时也要继续轮询（任务可追溯的核心）。
 */

import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  bumpFolderRefreshTick,
  markFolderArchivedComplete,
  selectActiveTasks,
  taskCompleted,
  taskProgressUpdated,
  taskTimedOut
} from '../../../slices/aiClassify'
import { fetchFolderStatistic } from '../../../actions/folder'

const POLL_INTERVAL_MS = 10_000
// 3 分钟 timeout · photoBook 后台扫描 1-2 分钟内出结果，3 分钟仍 0/未达 90% 视为
// 扫描已完成（rule 没匹配到照片）。原 10 分钟太长，用户看到"添加中"徽标半小时
// 不变会以为系统卡死。
const TIMEOUT_MS = 3 * 60_000
/** 完成阈值 · LLM estimated_count 偏差容忍 */
const COMPLETION_RATIO = 0.9

interface FolderStatItem {
  folderID: number
  photoCount: number
  folderCount?: number
}

export function useArchiveTaskPolling(): void {
  const dispatch = useAppDispatch()
  const activeTasks = useAppSelector(selectActiveTasks)
  const groupID = useAppSelector((s) => s.group.groupId)
  // 用 ref 持有最新 activeTasks 给 setInterval 闭包内用，
  // 避免 effect 依赖变化反复重建 timer（任务多时频繁切换）
  const tasksRef = useRef(activeTasks)
  tasksRef.current = activeTasks

  useEffect(() => {
    if (!groupID) return
    if (activeTasks.length === 0) return

    const tick = async (): Promise<void> => {
      const tasks = tasksRef.current
      if (tasks.length === 0) return

      // 1. 先扫超时（即使接口失败也要让超时任务退出 archiving）
      const now = Date.now()
      for (const t of tasks) {
        if (now - t.createdAt > TIMEOUT_MS) {
          dispatch(taskTimedOut({ taskId: t.taskId }))
        }
      }

      // 2. 拉一次 statistic（剩下还在 archiving 的）
      const stillActive = tasksRef.current
      if (stillActive.length === 0) return
      const folderIDs = stillActive.map((t) => t.folderID)

      let stats: FolderStatItem[] = []
      try {
        const resp = (await dispatch(
          fetchFolderStatistic({ groupID, folderIDs })
        )) as { data?: FolderStatItem[] } | FolderStatItem[] | undefined
        // fetchFolderStatistic 返回 res.data；这里再兼容直接数组的情况
        if (Array.isArray(resp)) stats = resp
        else if (resp && Array.isArray(resp.data)) stats = resp.data
      } catch {
        // 网络失败 silently 跳过本轮，下次再试
        return
      }

      // 3. 按 folderID 比对 task，更新进度 / 标记完成
      let photoCountChanged = false
      let anyCompleted = false
      for (const stat of stats) {
        const task = stillActive.find((t) => t.folderID === stat.folderID)
        if (!task) continue
        const photoCount = Number(stat.photoCount ?? 0) || 0
        if (photoCount !== task.currentCount) photoCountChanged = true
        const meetsTarget =
          task.expectedCount > 0
            ? photoCount >= task.expectedCount * COMPLETION_RATIO
            : photoCount > 0
        if (meetsTarget) {
          anyCompleted = true
          dispatch(
            taskCompleted({
              taskId: task.taskId,
              currentCount: photoCount,
              completedAt: Date.now()
            })
          )
          // 归档完成 · folder 此时挂 NEW 角标 · 与"添加中"徽章互斥
          dispatch(
            markFolderArchivedComplete({ folderId: String(task.folderID) })
          )
        } else {
          dispatch(
            taskProgressUpdated({
              taskId: task.taskId,
              currentCount: photoCount
            })
          )
        }
      }

      // 任一文件夹的 photoCount 变了 → 触发左侧 FolderList 重拉 statistic，
      // 让卡片上的"X 张"实时刷新（建夹后从 0 涨到真实数字 / 任务完成时归位）。
      // 仅在有变化时 bump，避免无谓重拉（FolderList 拉一次是 fetchGroupFolders +
      // fetchFolderStatistic 两段串行 RPC）。
      // preserveRecentlyCreated=true：保留刚建夹时的进场动画 ids，不被轮询清掉。
      if (photoCountChanged || anyCompleted) {
        dispatch(bumpFolderRefreshTick({ preserveRecentlyCreated: true }))
      }
    }

    // 立即跑一次（创建后第一时间反馈），然后再起 interval
    void tick()
    const id = window.setInterval(tick, POLL_INTERVAL_MS)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTasks.length, groupID, dispatch])
}
